import React, { useEffect, useState } from 'react';
import { useHasMoreBelow } from '../../hooks/useHasMoreBelow';
import { useIsScrolling } from '../../hooks/useIsScrolling';
import { eventHistoryService, type HistoryEventItemDto } from '../../services/eventHistoryService';
import { SkeletonBar } from '../../ui/Skeleton/SkeletonBar';

export interface InlineHistoryTimelineLabels {
    loading: string;
    error: string;
}

interface InlineHistoryTimelineProps {
    // Events are customer-scoped: GET /events only filters by customer, so this is the key.
    customerId: string;
    labels: InlineHistoryTimelineLabels;
    // Entries the caller already knows this customer has: 0 skips the box and the request, and a
    // positive count reserves that height while the fetch is in flight so the card never resizes.
    entryCount: number;
    // Renders one history entry. `index` is its 0-based position in the (filtered, ordered) list and
    // `events` is that full list — together they let the caller derive a chronological ordinal even
    // when the list is ordered newest-first.
    renderEntry: (event: HistoryEventItemDto, index: number, events: HistoryEventItemDto[]) => React.ReactNode;
    // Restrict to a single event type (e.g. 'RELANCE_COMPLETED'). Omitted → every type is shown.
    eventType?: HistoryEventItemDto['type'];
    // Sort on occurredAt. Defaults to 'asc' (oldest → newest) here, independently of the service's own
    // 'desc' default; the reminder follow-up opts into 'desc'.
    orderDirection?: 'asc' | 'desc';
    pageSize?: number;
    testId?: string;
}

// A client realistically accumulates a handful of events; one generous page covers every real
// case, so the timeline reads a single page rather than paginating.
const DEFAULT_PAGE_SIZE = 100;

type LoadState = 'loading' | 'error' | 'ready';

// Generic read-only history box shown inline on a card. Domain-specific copy and entry layout are
// supplied by the caller, so any card with a notion of history can reuse it.
export const InlineHistoryTimeline: React.FC<InlineHistoryTimelineProps> = ({
    customerId,
    labels,
    entryCount,
    renderEntry,
    eventType,
    orderDirection = 'asc',
    pageSize = DEFAULT_PAGE_SIZE,
    testId = 'inline-history',
}) => {
    const [state, setState] = useState<LoadState>('loading');
    const [events, setEvents] = useState<HistoryEventItemDto[]>([]);
    const { ref: scrollRef, hasMoreBelow, onScroll: updateMoreBelow } = useHasMoreBelow<HTMLDivElement>();
    const { isScrolling, onScroll: markScrolling } = useIsScrolling();

    useEffect(() => {
        if (entryCount === 0) return;
        let cancelled = false;
        setState('loading');
        eventHistoryService
            .getHistory({ customerId, page: 1, pageSize, eventType, orderDirection })
            .then(data => {
                if (cancelled) return;
                setEvents(eventType ? data.items.filter(e => e.type === eventType) : data.items);
                setState('ready');
            })
            .catch(() => {
                if (!cancelled) setState('error');
            });
        return () => {
            cancelled = true;
        };
    }, [customerId, entryCount, eventType, orderDirection, pageSize]);

    if (entryCount === 0 || (state === 'ready' && events.length === 0)) return null;

    return (
        <div className="relative text-xs font-semibold rounded-lg bg-surface-secondary overflow-hidden pb-xs" data-testid={testId}>
            {/* A long history scrolls inside the box rather than stretching the card. The 160px cap is
                exactly three entries (pt 12 + 3 × 36 + 2 × 14 gaps + pb 12), so a fourth starts just out
                of view; the outer 4px of padding keeps any scrolled text off the box's edge. */}
            <div
                ref={scrollRef}
                onScroll={() => {
                    updateMoreBelow();
                    markScrolling();
                }}
                data-scrolling={isScrolling || undefined}
                className="px-m pt-m pb-m max-h-40 overflow-y-auto scrollbar-subtle"
            >
                {state === 'loading' && (
                    <ul className="flex flex-col gap-3.5" aria-busy="true">
                        <li className="sr-only">{labels.loading}</li>
                        {Array.from({ length: entryCount }, (_, i) => (
                            <li key={i} aria-hidden="true">
                                <SkeletonBar className="h-lh w-3/4" />
                                <SkeletonBar className="h-lh w-1/2" />
                            </li>
                        ))}
                    </ul>
                )}
                {state === 'error' && <p className="text-danger" data-testid={`${testId}-error`}>{labels.error}</p>}
                {state === 'ready' && (
                    <ul className="flex flex-col gap-3.5" data-testid={`${testId}-list`}>
                        {events.map((event, index) => (
                            <li key={event.id}>{renderEntry(event, index, events)}</li>
                        ))}
                    </ul>
                )}
            </div>
            {hasMoreBelow && (
                // Fades only the entries' left column, where the timeline rule runs on past the visible
                // area; it sits over the 5px bullet column and leaves the text untouched.
                <div
                    aria-hidden="true"
                    data-testid={`${testId}-fade`}
                    className="pointer-events-none absolute left-m bottom-xs w-1.25 h-6 bg-linear-to-t from-surface-secondary to-transparent"
                />
            )}
        </div>
    );
};
